import clsx from "clsx";
import { CSSProperties } from "react";
import { renderToStaticMarkup } from "react-dom/server";

import { PIC } from "@ryde/assets";
import { greyMui } from "@ryde/theme";

import { DirectionEnum } from "../enums";

/** Default pin fill when course tracking marker has no line color. */
const COURSE_CODE_MARKER_DEFAULT_PIN_COLOR = greyMui._500;

export enum MarkerIconType {
  Home = "home",
  Point = "point",
  Flag = "flag",
  DropOff = "DropOff",
}

export type MapMarkerIconVariant = "courseCode";

export const getMarkerIconClassName = ({
  type,
  colorId,
  variant,
  text,
  inDetour,
}: {
  type: MarkerIconType;
  colorId?: number;
  variant?: MapMarkerIconVariant;
  text?: string;
  inDetour?: boolean;
}): string => {
  return clsx("map-marker-icon", {
    [`map-marker-icon-${colorId}`]: colorId && variant !== "courseCode",
    "map-marker-icon--course-code": variant === "courseCode",
    "map-marker-icon--course-solid": variant === "courseCode" && !text?.trim(),
    "map-marker-icon--detour": variant === "courseCode" && inDetour,
    "map-marker-icon-dropOff": type === MarkerIconType.DropOff,
    "map-marker-icon-home": type === MarkerIconType.Home,
    "map-marker-icon-flag": type === MarkerIconType.Flag,
  });
};

export const serializeIcon = ({
  type,
  text,
  color,
  badgeColor,
  outlineColor,
  textColor,
  variant,
  inDetour,
}: {
  type: MarkerIconType;
  text?: string;
  color?: string;
  badgeColor?: string;
  /** Pin outline / border (live map course-code tracking marker). */
  outlineColor?: string;
  /** Digits color for course-code tracking marker. */
  textColor?: string;
  variant?: MapMarkerIconVariant;
  /** Course pin only: render the pulsing detour ring (mirrors car indicator). */
  inDetour?: boolean;
}): string => {
  const isCourseCode = variant === "courseCode";
  const pinFillColor = isCourseCode
    ? (color ?? COURSE_CODE_MARKER_DEFAULT_PIN_COLOR)
    : color;

  if (isCourseCode) {
    const coursePinStyles: CSSProperties = { color: pinFillColor };
    const textStyles: CSSProperties = textColor ? { color: textColor } : {};

    return renderToStaticMarkup(
      <div
        className="map-marker-icon-container map-marker-icon-course-pin-wrap"
        style={coursePinStyles}
      >
        {inDetour && (
          <div className="map-marker-icon-course-pin-pulse" aria-hidden />
        )}
        <PIC.CourseTrackingPinSVG
          className="map-marker-icon-course-pin-svg"
          aria-hidden
        />
        {Boolean(text?.trim()) && (
          <div className="map-marker-icon-text" style={textStyles}>
            {text}
          </div>
        )}
      </div>,
    );
  }

  const styles: CSSProperties = pinFillColor
    ? { backgroundColor: pinFillColor }
    : {};
  if (outlineColor) {
    styles.boxShadow = `0 0 0 2px ${outlineColor}`;
  }
  const badgeStyles: CSSProperties = badgeColor
    ? { backgroundColor: badgeColor }
    : {};
  const bgAvaStyles: CSSProperties = color ? { color } : {};
  const textStylesMarkerIcon: CSSProperties = textColor
    ? { color: textColor }
    : {};

  return renderToStaticMarkup(
    <div className="map-marker-icon-container">
      <div className="map-marker-icon-inner" style={styles}>
        {type === MarkerIconType.DropOff && (
          <div className="map-marker-icon-bg-ava" style={bgAvaStyles}>
            <PIC.FinishSVG />
          </div>
        )}
        {type === MarkerIconType.Home && (
          <div className="map-marker-icon-ava">
            <PIC.HomeSVG />
          </div>
        )}
        {type === MarkerIconType.Flag && (
          <div className="map-marker-icon-ava">
            <PIC.FlagSVG />
          </div>
        )}
        {type === MarkerIconType.Point && Boolean(text?.trim()) && (
          <div className="map-marker-icon-text" style={textStylesMarkerIcon}>
            {text}
          </div>
        )}
      </div>
      {type === MarkerIconType.DropOff && !!text && (
        <div className="map-marker-icon-badge" style={badgeStyles}>
          <div className="map-marker-icon-badge-text">{text}</div>
        </div>
      )}
    </div>,
  );
};

export const getIconMarkerTypeByRoute = ({
  isDropoff,
  isLastWayPoint,
  direction,
}: {
  isDropoff?: boolean;
  isLastWayPoint: boolean;
  direction?: DirectionEnum;
}): MarkerIconType => {
  if (direction === DirectionEnum.DropOff) {
    if (isLastWayPoint) {
      return MarkerIconType.DropOff;
    }

    return MarkerIconType.Point;
  }

  if (isLastWayPoint) {
    return MarkerIconType.DropOff;
  }

  return isDropoff ? MarkerIconType.DropOff : MarkerIconType.Point;
};
